import type { GoogleWorkspacePermissionStatus } from '@oracord/shared';
import type { BadgeTone } from './web-ui-style';

export type StaffGoogleAuthorizationAction =
  | 'admin_setup_needed'
  | 'authorization_pending'
  | 'reconnect_pending'
  | 'connected'
  | 'not_connected';

export interface StaffGoogleAuthorizationSummary {
  action: StaffGoogleAuthorizationAction;
  title: string;
  detail: string;
  nextAction: string;
  badge: string;
  tone: BadgeTone;
  gmailLabel: string;
  calendarLabel: string;
  primaryActionLabel: string;
  primaryActionEnabled: boolean;
  complete: boolean;
  privacyNote: string;
}

const PRIVACY_NOTE =
  'This is staff-approved work-data permission setup only. This page does not scan Gmail, sync Calendar, store Google tokens, or expose raw email/calendar content to managers.';

export function buildStaffGoogleAuthorizationSummary(
  permissions: GoogleWorkspacePermissionStatus,
): StaffGoogleAuthorizationSummary {
  const gmailLabel = permissions.gmail ? 'Permission recorded' : 'Not connected';
  const calendarLabel = permissions.calendar ? 'Permission recorded' : 'Not connected';

  if (permissions.state === 'needs_admin_config') {
    return {
      action: 'admin_setup_needed',
      title: 'Google authorization needs admin setup.',
      detail:
        'Your staff profile is matched, but this organization has not configured Google Workspace authorization for Gmail and Calendar yet.',
      nextAction: 'Ask an admin to finish Google setup before staff can authorize work-data permissions.',
      badge: 'Admin setup needed',
      tone: 'slate',
      gmailLabel,
      calendarLabel,
      primaryActionLabel: 'Waiting on admin setup',
      primaryActionEnabled: false,
      complete: false,
      privacyNote: PRIVACY_NOTE,
    };
  }

  if (permissions.state === 'needs_staff_authorization') {
    return {
      action: 'authorization_pending',
      title: 'Approve Gmail and Calendar access.',
      detail:
        'Use your work Google account to approve read-only Gmail and Calendar context for Oracord. This is the real authorization step after the admin adds you to the company.',
      nextAction: 'Continue to Google and approve Gmail/Calendar access.',
      badge: 'Consent needed',
      tone: 'amber',
      gmailLabel,
      calendarLabel,
      primaryActionLabel: 'Authorize Gmail and Calendar',
      primaryActionEnabled: true,
      complete: false,
      privacyNote: PRIVACY_NOTE,
    };
  }

  if (permissions.state === 'connected') {
    return {
      action: 'connected',
      title: 'Google authorization is recorded.',
      detail:
        'Oracord has a source-honest permission status for Gmail and Calendar on your staff profile. This is readiness state, not proof of live ingestion.',
      nextAction: 'No action is needed here unless Google later reports missing, expired, or revoked access.',
      badge: 'Connected',
      tone: 'emerald',
      gmailLabel,
      calendarLabel,
      primaryActionLabel: 'No action needed',
      primaryActionEnabled: false,
      complete: true,
      privacyNote: PRIVACY_NOTE,
    };
  }

  if (permissions.state === 'needs_reconnect') {
    return {
      action: 'reconnect_pending',
      title: 'Google authorization needs reconnect.',
      detail:
        'One or more expected Gmail or Calendar permissions are missing or stale. Reconnect will require staff approval when the consent step is available.',
      nextAction: 'Reconnect by approving Google permissions again.',
      badge: 'Needs reconnect',
      tone: 'amber',
      gmailLabel,
      calendarLabel,
      primaryActionLabel: 'Reconnect Gmail and Calendar',
      primaryActionEnabled: true,
      complete: false,
      privacyNote: PRIVACY_NOTE,
    };
  }

  if (permissions.state === 'revoked') {
    return {
      action: 'reconnect_pending',
      title: 'Google authorization was revoked.',
      detail:
        'Oracord records Google work-data permission as revoked for this staff profile. Nothing here restarts access or reads Gmail/Calendar content.',
      nextAction: 'Reconnect only after you intentionally approve Google permissions again.',
      badge: 'Revoked',
      tone: 'amber',
      gmailLabel,
      calendarLabel,
      primaryActionLabel: 'Reconnect Gmail and Calendar',
      primaryActionEnabled: true,
      complete: false,
      privacyNote: PRIVACY_NOTE,
    };
  }

  return {
    action: 'not_connected',
    title: 'Google authorization is not connected yet.',
    detail:
      'Your staff profile is matched, but Oracord has not recorded staff-approved Gmail or Calendar permissions yet.',
    nextAction: 'Connect Gmail and Calendar permissions when your organization enables this setup step.',
    badge: 'Not connected',
    tone: 'slate',
    gmailLabel,
    calendarLabel,
    primaryActionLabel: 'Authorization step not live yet',
    primaryActionEnabled: false,
    complete: false,
    privacyNote: PRIVACY_NOTE,
  };
}
